import axios from "axios";
import { useEffect, useRef, useState } from "react";
import useGlobalContext from "../../hooks/useGlobalContext";
import {
  downloadBlob,
  getCompanyInformation,
} from "../../common/EntitiesManagement";
import localize from "../../common/localize";
import LoadingPanel from "../../components/common/LoadingPanel";
import FieldTextField from "../../components/common/FieldTextfield";
import ModalBackground from "../../components/common/ModalBackground";
import BackgroundForegroundColorPicker from "../../components/common/BackgroundForegroundColorPicker";
import Combobox from "../../components/common/Combobox";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
} from "@mui/material";
import { getInputLabelProps } from "../../common/appStyles";
import {
  getEvidenceTypeExtension,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
  applyAppColors,
} from "../../common/utils";
import ApplicationLogoVertical from "../../components/common/ApplicationLogoVertical";
import { Close, UploadFile } from "@mui/icons-material";

export default function CompanyData({ showCompanyModal, setShowCompanyModal }) {
  const [descriptionCount, setDescriptionCount] = useState(0);
  const { COMPANY_ENDPOINT, config } = useGlobalContext();
  const [companyInfo, setCompanyInfo] = useState(getCompanyInformation());
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const inputFile = useRef(null);
  const [logoBlob, setLogoBlob] = useState(undefined);
  const [isLoadingLogo, setIsLoadingLogo] = useState(false);

  const getCompanyData = async () => {
    setLoading(true);
    try {
      const companyRequestResult = await axios.get(
        `${COMPANY_ENDPOINT}`,
        config,
      );

      if (companyRequestResult?.data?.Ok && companyRequestResult?.data?.Data) {
        setCompanyInfo({
          ...companyRequestResult?.data?.Data,
          FileName:
            companyRequestResult?.data?.Data?.LogoPath?.split("/")?.pop(),
          CreatedBy: getCompanyInformation().CreatedBy,
        });
      }

      if (companyRequestResult?.data?.Data?.LogoPath) {
        getLogoBlob(companyRequestResult?.data?.Data?.Id);
      }
    } finally {
      setLoading(false);
    }
  };

  const getLogoBlob = (companyId) => {
    if (companyId > 0) {
      downloadBlob(
        `${COMPANY_ENDPOINT}/${companyId}/logo/download`,
        config,
        setLogoBlob,
        setIsLoadingLogo,
      );
    }
  };

  useEffect(() => {
    if (showCompanyModal) {
      getCompanyData();
    }
  }, [showCompanyModal]);

  useEffect(() => {
    if (companyInfo?.ComplianceText) {
      setDescriptionCount(companyInfo?.ComplianceText.length);
    }
  }, [companyInfo?.ComplianceText]);

  const updateCompany = async (data) => {
    const dataToSave = {
      ...data,
      ReportBackgroundColorHex: data?.ReportBackgroundColorHex ?? "#10366e",
      ReportForegroundColorHex: data?.ReportForegroundColorHex ?? "#ffffff",
      UseColorBranding: data?.UseColorBranding ?? 0,
      UseCustomLogo: data?.UseCustomLogo ?? 0,
      File: undefined,
      FileName: undefined,
    };

    const formData = new FormData();
    formData.append(
      "requestData",
      new Blob([JSON.stringify(dataToSave)], {
        type: "application/json",
      }),
    );

    if (data?.File) {
      formData.append("document", data?.File, data?.FileName);
    }

    const requestConfig = {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    };

    const result = await axios.put(
      `${COMPANY_ENDPOINT}/${dataToSave.Id}${data?.File ? "/logo" : ""}`,
      formData,
      requestConfig,
    );

    validateOperationResult(result);
  };

  const createCompany = async (data) => {
    const dataToSave = {
      ...data,
      ReportBackgroundColorHex: data?.ReportBackgroundColorHex ?? "#10366e",
      ReportForegroundColorHex: data?.ReportForegroundColorHex ?? "#ffffff",
      UseColorBranding: data?.UseColorBranding ?? 0,
      UseCustomLogo: data?.UseCustomLogo ?? 0,
      File: undefined,
      FileName: undefined,
    };

    const formData = new FormData();
    formData.append(
      "requestData",
      new Blob([JSON.stringify(dataToSave)], {
        type: "application/json",
      }),
    );

    if (data?.File) {
      formData.append("document", data?.File, data?.FileName);
    }

    const requestConfig = {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    };

    const result = await axios.post(
      `${COMPANY_ENDPOINT}${data?.File ? "/logo" : ""}`,
      formData,
      requestConfig,
    );
    validateOperationResult(result);
  };

  const validateOperationResult = (result) => {
    if (!result.data?.Ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    if (Number(companyInfo?.UseColorBranding) === 1) {
      applyAppColors(
        companyInfo?.ReportBackgroundColorHex,
        companyInfo?.ReportForegroundColorHex,
      );
    } else {
      applyAppColors();
    }
    setShowCompanyModal(false);
  };

  const saveCompanyInformation = () => {
    if (companyInfo.Id > 0) {
      updateCompany(companyInfo);
      return;
    }

    createCompany(companyInfo);
  };

  const handleTextFieldChange = (e) => {
    setCompanyInfo({
      ...companyInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleChange = (e) => {
    if (e.target.name === "Value" && e.target.value < 0) {
      setCompanyInfo({
        ...companyInfo,
        [e.target.name]: 0,
      });
      return;
    }

    setCompanyInfo({
      ...companyInfo,
      [e.target.name]: e.target.value,
    });
  };

  const onSelectButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  const changeFilePath = (e) => {
    if (e.target.files) {
      setCompanyInfo({
        ...companyInfo,
        LogoPath: e.target.files[0].name,
        File: e.target.files[0],
        FileName: e.target.files[0].name,
      });

      setLogoBlob(URL.createObjectURL(e.target.files[0]));
      return;
    }

    setCompanyInfo({
      ...companyInfo,
      File: undefined,
      FileName: undefined,
    });
  };

  const getBrandingOptions = () => [
    { Id: 0, Name: localize.branding_apply_reports_only },
    { Id: 1, Name: localize.branding_apply_reports_and_app },
  ];

  const getBrandingSelected = (value) =>
    getBrandingOptions().find((option) => option.Id === (value ?? 0)) ??
    getBrandingOptions()[0];

  const handleBrandingChange = (propertyName) => (selected) => {
    setCompanyInfo({
      ...companyInfo,
      [propertyName]: selected?.Id ?? 0,
    });
  };

  const getLogoComponent = () => {
    return (
      <Box className="flex flex-col gap-2 w-full">
        <input
          type="file"
          id="file"
          accept={getEvidenceTypeExtension(1)}
          ref={inputFile}
          onChange={changeFilePath}
          style={{ display: "none" }}
        />

        <Box className="flex justify-center w-full">
          {isLoadingLogo ? (
            <CircularProgress size={20} />
          ) : (
            <ApplicationLogoVertical
              height={80}
              width={100}
              path={
                !companyInfo?.LogoPath ||
                companyInfo?.LogoPath.replace(" ", "") === ""
                  ? `${process.env.PUBLIC_URL}/img/app_report_header.png`
                  : logoBlob ??
                    `${process.env.PUBLIC_URL}/img/app_report_header.png`
              }
            />
          )}
        </Box>

        <Box className="flex flex-col gap-2 w-full">
          <Box className="flex flex-row gap-2 w-full justify-center">
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadFile />}
              onClick={onSelectButtonClick}
              sx={{
                fontSize: "0.75rem",
                backgroundColor: "var(--app-bg, #10366e)",
                color: "var(--app-fg, #ffffff)",
                "&:hover": { backgroundColor: "var(--app-bg, #10366e)" },
              }}
            >
              {localize.load}
            </Button>

            {companyInfo?.FileName && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<Close />}
                sx={{ fontSize: "0.75rem" }}
                onClick={() => {
                  setCompanyInfo({
                    ...companyInfo,
                    File: undefined,
                    FileName: undefined,
                    LogoPath: undefined,
                  });

                  setLogoBlob(undefined);
                }}
              >
                {localize.delete}
              </Button>
            )}
          </Box>

          {companyInfo?.FileName && (
            <label className="report_little_text report_disabled_text text-center">
              {companyInfo?.FileName.replace(/^[^_]+_/, "")}
            </label>
          )}
        </Box>
      </Box>
    );
  };

  const formData = () => {
    return (
      <div className="flex-col flex" style={{ width: "100%" }}>
        <Box className="w-full mb-2">
          <label className="text-xs">
            <b>{localize.general_information}</b>
          </label>
          <Divider sx={{ mt: 0.5 }} />
        </Box>

        <FieldTextField
          isForDrawer
          isRequired
          titleClassName="text-xs"
          fieldName="Identification"
          fieldTitle={localize.identification}
          fieldSubtitle={localize.company_identification}
          handleValueChange={handleTextFieldChange}
          fieldValue={companyInfo?.Identification ?? ""}
          customMinWidth="60%"
        />

        <FieldTextField
          isForDrawer
          isRequired
          titleClassName="text-xs"
          fieldName="Name"
          fieldTitle={localize.company_name}
          handleValueChange={handleTextFieldChange}
          fieldValue={companyInfo?.Name ?? ""}
          customMinWidth="60%"
        />

        <FieldTextField
          isForDrawer
          titleClassName="text-xs"
          fieldName="Address"
          fieldTitle={localize.address}
          handleValueChange={handleTextFieldChange}
          fieldValue={companyInfo?.Address ?? ""}
          customMinWidth="60%"
        />

        <FieldTextField
          isForDrawer
          titleClassName="text-xs"
          fieldName="PhoneNumber"
          fieldTitle={localize.phone_number}
          handleValueChange={handleTextFieldChange}
          fieldValue={companyInfo?.PhoneNumber ?? ""}
          customMinWidth="60%"
        />

        <FieldTextField
          isForDrawer
          titleClassName="text-xs"
          fieldName="Email"
          fieldTitle={localize.email}
          handleValueChange={handleTextFieldChange}
          fieldValue={companyInfo?.Email ?? ""}
          customMinWidth="60%"
        />

        <Divider sx={{ my: 1 }} />

        <Box className="my-2 w-full">
          <TextField
            InputLabelProps={getInputLabelProps()}
            inputProps={{
              style: {
                fontSize: "0.8em",
              },
              maxLength: MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
            }}
            size="small"
            fullWidth
            multiline
            rows={4}
            id="outlined-basic"
            name="ComplianceText"
            label={localize.complianceText}
            variant="outlined"
            onChange={handleChange}
            value={companyInfo?.ComplianceText}
            helperText={`${descriptionCount}/${MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION}`}
          />
        </Box>

        <Box className="w-full mt-3 mb-2">
          <label className="text-xs">
            <b>{localize.brand_colors}</b>
          </label>
          <Divider sx={{ mt: 0.5 }} />
        </Box>

        <Box className="flex flex-col gap-1 my-2 w-full">
          <label className="text-xs">
            <b>{localize.report_section_title_colors}</b>
          </label>
          <Combobox
            id="UseColorBranding"
            fieldLabel=""
            fieldOptions={getBrandingOptions()}
            fieldValue={getBrandingSelected(companyInfo?.UseColorBranding)}
            handleValueChange={handleBrandingChange("UseColorBranding")}
          />
          <BackgroundForegroundColorPicker
            backgroundValue={companyInfo?.ReportBackgroundColorHex ?? "#10366e"}
            foregroundValue={companyInfo?.ReportForegroundColorHex ?? "#ffffff"}
            backgroundName="ReportBackgroundColorHex"
            foregroundName="ReportForegroundColorHex"
            onChange={handleChange}
            previewText={localize.title}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box className="flex flex-col gap-2 my-2 w-full">
          <label className="text-xs font-bold">{localize.custom_logo}</label>
          <Combobox
            id="UseCustomLogo"
            fieldLabel=""
            fieldOptions={getBrandingOptions()}
            fieldValue={getBrandingSelected(companyInfo?.UseCustomLogo)}
            handleValueChange={handleBrandingChange("UseCustomLogo")}
          />
          {Number(companyInfo?.UseCustomLogo) === 1 && (
            <Alert severity="info" className="w-full">
              <span className="text-xs">{localize.logo_recommended_size}</span>
            </Alert>
          )}
          {getLogoComponent()}
        </Box>

        {errorMessage && (
          <Alert className="w-full" severity="error">
            <label className="text-xs">{errorMessage}</label>
          </Alert>
        )}
      </div>
    );
  };

  const disableSaveChanges = () =>
    !companyInfo.Identification ||
    companyInfo.Identification.replaceAll(" ", "").length <= 0 ||
    !companyInfo.Name ||
    companyInfo.Name.replaceAll(" ", "").length <= 0;

  const showModal = () => {
    return (
      <div>
        <ModalBackground
          hasRequiredValues
          isDrawerMode
          showModal={showCompanyModal}
          setShowModal={setShowCompanyModal}
          onClickToCancel={() => {
            setShowCompanyModal(false);
          }}
          onClickToSubmit={() => {
            saveCompanyInformation();
          }}
          title={localize.company_information}
          Component={
            <div>{loading ? <LoadingPanel donotUseHscreen /> : formData()}</div>
          }
          validators={disableSaveChanges()}
        />
      </div>
    );
  };

  return showModal();
}
